import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { CMSContent } from '../components/cms/CMSContent';
import SEO from '../components/SEO';

/**
 * LegalDocumentPage — generic public renderer for a canonical legal
 * document resolved by content_key. Reuses the existing public resolver
 * (GET /api/public/content/resolve via CMSContent → useContent), which
 * serves ONLY the current published version and honours the canonical
 * locale/fallback rules. Never exposes drafts or arbitrary versions.
 *
 * LEGAL-PUBLIC-FOOTER-PROCESS-01 — one generic route, not one page per
 * document.
 *
 * TREATX-LEGAL-PDF-DOWNLOAD-01 — the Download PDF action is a generic
 * capability of this one renderer: it asks the backend for a PDF of the
 * SAME published body, for every document served here. No per-document
 * special case, no static duplicate file.
 */
const LegalDocumentPage = () => {
  const { contentKey } = useParams();
  const { t, i18n } = useTranslation();
  const language = (i18n.language || 'en').split('-')[0];
  const pdfHref =
    `${process.env.REACT_APP_BACKEND_URL}/api/public/content/${encodeURIComponent(contentKey)}/pdf` +
    `?language=${encodeURIComponent(language)}`;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" data-testid="legal-document-page">
      <SEO title="Legal" canonicalPath={`/legal/${contentKey}`} />
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 sm:p-8 lg:p-10">
        <div className="flex justify-end mb-4">
          <a
            href={pdfHref}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
            data-testid="legal-download-pdf"
          >
            <Download className="w-4 h-4" />
            {t('legal.downloadPdf', 'Download PDF')}
          </a>
        </div>
        <CMSContent
          contentKey={contentKey}
          showTitle={true}
          showSummary={false}
          showLegalMetadata={true}
        />
      </div>
    </div>
  );
};

export default LegalDocumentPage;
